// AttentionPanel — "Needs attention" at the top of the case detail (RRA v2.1 A2).
//
// One plain list: every reason this case needs staff, stored events first, then
// the derived ones (lib/rra/rraCaseView.ts attentionReasons). A stored entry
// carries "Got it"; a possible-application entry carries "Attach", which asks
// before it binds. Nothing here shows an id — the entry key and the application
// ids only travel in the request. Renders nothing when there is nothing to do.

import { useState } from 'react';
import { attachRraCase, clearRraAttention, type RraAttentionView, type RraCaseView } from '@/lib/rraStaffClient';

/** The console's action runner (AccommodationsPage `run`): busy flag, error banner, reload — and a notice, fixed or computed from the result. */
export type RraRunFn = (
  key: string,
  fn: () => Promise<unknown>,
  successNotice?: string | ((result: unknown) => string | undefined),
) => Promise<void>;

export function AttentionPanel({
  record,
  busy,
  run,
}: {
  record: RraCaseView;
  busy: string | null;
  run: RraRunFn;
}): React.JSX.Element | null {
  const [confirmKey, setConfirmKey] = useState<string | null>(null);
  const entries: RraAttentionView[] = record.attention ?? [];
  if (entries.length === 0) return null;

  return (
    <div className="card" data-section="rra-attention" style={{ borderColor: 'var(--danger)' }}>
      <div style={{ fontWeight: 'var(--weight-medium)', marginBottom: 'var(--space-3)' }}>Needs attention</div>
      <ul className="stack-sm" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {entries.map((entry) => {
          const attach = entry.attach;
          const confirming = confirmKey === entry.key && attach !== undefined;
          return (
            <li key={entry.key}>
              <div
                className="cluster"
                style={{ gap: 'var(--space-3)', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}
              >
                <span style={{ fontSize: 'var(--text-sm)', minWidth: 0, overflowWrap: 'anywhere' }}>{entry.text}</span>
                <span className="cluster" style={{ gap: 'var(--space-2)' }}>
                  {attach && !confirming && (
                    <button
                      type="button"
                      className="btn"
                      data-variant="secondary"
                      data-size="sm"
                      disabled={busy !== null}
                      onClick={() => setConfirmKey(entry.key)}
                    >
                      Attach
                    </button>
                  )}
                  {entry.clearable && (
                    <button
                      type="button"
                      className="btn"
                      data-variant="ghost"
                      data-size="sm"
                      disabled={busy !== null}
                      onClick={() =>
                        void run(`attention-clear:${entry.key}`, () => clearRraAttention(record.caseId, entry.key))
                      }
                    >
                      Got it
                    </button>
                  )}
                </span>
              </div>
              {confirming && attach && (
                <div className="stack-sm" style={{ marginTop: 'var(--space-2)' }}>
                  <div style={{ fontSize: 'var(--text-sm)' }}>{`Attach this request to ${attach.label}?`}</div>
                  <div className="cluster" style={{ gap: 'var(--space-2)' }}>
                    <button
                      type="button"
                      className="btn"
                      data-variant="primary"
                      data-size="sm"
                      disabled={busy !== null}
                      onClick={() =>
                        void run(
                          `attention-attach:${entry.key}`,
                          async () => {
                            const res = await attachRraCase(record.caseId, {
                              applicationGroupId: attach.applicationGroupId,
                              ...(attach.personKey ? { personKey: attach.personKey } : {}),
                            });
                            setConfirmKey(null);
                            return res;
                          },
                          'Attached to the application. The freeze is now in place.',
                        )
                      }
                    >
                      {busy === `attention-attach:${entry.key}` ? 'Attaching…' : 'Attach'}
                    </button>
                    <button
                      type="button"
                      className="btn"
                      data-variant="ghost"
                      data-size="sm"
                      disabled={busy !== null}
                      onClick={() => setConfirmKey(null)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
